import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowUpRight, Phone, Check, Zap, Layers, Scissors, PenTool,
  Lightbulb, FileCheck2, Printer, Truck, Handshake, BadgeCheck, SlidersHorizontal, Palette,
} from "lucide-react";
import { IMAGES, COMPANY } from "../lib/site";
import { Reveal } from "../components/Reveal";
import { Eyebrow, SectionHeader, CTAButton, useSeo } from "../components/Shared";

const SERVICE_AREAS = [
  {
    icon: Zap,
    img: IMAGES.printDigitalCard,
    alt: "Large-format digital printing press producing colorful commercial materials",
    t: "Digital Printing",
    d: "Fast, flexible production ideal for shorter runs, quick-turn projects, personalized materials, and frequently updated content.",
  },
  {
    icon: Layers,
    img: IMAGES.printOffsetCard,
    alt: "Printed sheets moving through a commercial offset press",
    t: "Offset Printing",
    d: "High-quality, consistent production designed for larger quantities and projects where color consistency and unit cost are important.",
  },
  {
    icon: Scissors,
    img: IMAGES.printFinishingCard,
    alt: "Commercial print production equipment finishing printed sheets",
    t: "Finishing",
    d: "Professional finishing options that transform printed sheets into polished, ready-to-use marketing and business materials.",
  },
  {
    icon: PenTool,
    img: IMAGES.printDesignCard,
    alt: "Colorful paper swatch fan used for print design and stock selection",
    t: "In-House Graphic Design",
    d: "Creative support to develop new materials, refine existing artwork, or prepare files properly for professional printing.",
  },
];

const DIGITAL_APPS = [
  "Brochures", "Flyers", "Postcards", "Business Cards", "Sell Sheets", "Presentation Materials",
  "Invitations", "Booklets", "Marketing Collateral", "Training Materials", "Short-Run Publications",
  "Personalized Materials", "Variable-Data Printing Projects",
];

const OFFSET_APPS = [
  "High-Volume Brochures", "Catalogs", "Corporate Publications", "Direct Mail", "Presentation Folders",
  "Marketing Campaign Materials", "Booklets", "Stationery", "Business Forms", "Large Production Runs",
];

const FINISHING_OPTIONS = [
  "Cutting and Trimming", "Folding", "Scoring", "Creasing", "Binding", "Saddle Stitching",
  "Laminating", "Matte and Gloss Finishes", "Die Cutting", "Perforating", "Round Cornering",
  "Assembly", "Packaging",
];

const DESIGN_SERVICES = [
  "Brochure Design", "Flyer Design", "Business Cards", "Corporate Stationery", "Marketing Collateral",
  "Presentation Materials", "Event Materials", "Layout and Typesetting", "Artwork Modification",
  "File Preparation", "Print-Ready Artwork", "Prepress Assistance", "Brand-Consistent Design",
];

const PRINT_CATEGORIES = [
  {
    t: "Marketing Materials",
    items: ["Brochures", "Flyers", "Postcards", "Sell Sheets", "Presentation Folders", "Newsletters", "Catalogs", "Booklets"],
  },
  {
    t: "Business Essentials",
    items: ["Business Cards", "Letterhead", "Envelopes", "Notepads", "Forms", "Corporate Stationery", "Presentation Materials"],
  },
  {
    t: "Events & Promotions",
    items: ["Invitations", "Programs", "Event Materials", "Posters", "Cards", "Tickets", "Promotional Handouts"],
  },
  {
    t: "Specialty Projects",
    items: ["Labels", "Stickers", "Custom Printed Pieces", "Die-Cut Materials", "Specialty Finished Materials"],
  },
];

const PROCESS_STEPS = [
  { num: "01", icon: Lightbulb, t: "Concept", d: "Tell us what you're trying to create and what you want it to accomplish." },
  { num: "02", icon: PenTool, t: "Design", d: "Provide your existing artwork or work with MJG's graphic design team." },
  { num: "03", icon: FileCheck2, t: "Prepress", d: "Files are reviewed and prepared for professional production." },
  { num: "04", icon: Printer, t: "Print & Finish", d: "We coordinate the appropriate printing and finishing process for the project." },
  { num: "05", icon: Truck, t: "Deliver", d: "Your finished materials are prepared for use, distribution, your event, your sales team, or your customers." },
];

const WHY_BLOCKS = [
  { icon: Handshake, t: "One Partner", d: "Coordinate printing, promotional products, and creative services through one team." },
  { icon: BadgeCheck, t: "Professional Production", d: "Create polished materials that professionally represent your organization and brand." },
  { icon: SlidersHorizontal, t: "Flexible Solutions", d: "Select the production method appropriate for the quantity, timeline, specifications, and budget." },
  { icon: Palette, t: "Creative Support", d: "Get help designing new materials or preparing existing artwork for production." },
];

const Chip = ({ children }) => (
  <span className="border border-brand-line bg-brand-warm px-3 py-1.5 text-xs font-semibold text-brand-charcoal">{children}</span>
);

const ChipDark = ({ children }) => (
  <span className="border border-white/15 bg-white/5 px-3 py-1.5 text-xs font-semibold text-white/80">{children}</span>
);

const Hero = () => (
  <section className="relative overflow-hidden bg-brand-black pt-32 pb-20 sm:pt-40 sm:pb-28">
    <div className="absolute inset-0 grid-overlay opacity-40" aria-hidden="true" />
    <div className="absolute inset-0" aria-hidden="true">
      <img
        src={IMAGES.printHero}
        alt=""
        className="h-full w-full object-cover opacity-25"
        fetchPriority="high"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-brand-black via-brand-black/85 to-brand-black/40" />
    </div>
    <div className="relative mx-auto max-w-7xl px-5">
      <Reveal>
        <Eyebrow>Commercial Printing Services</Eyebrow>
      </Reveal>
      <motion.h1
        className="mt-5 max-w-4xl text-4xl font-extrabold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl"
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      >
        Professional Printing. <span className="text-brand-red">From Concept to Completion.</span>
      </motion.h1>
      <Reveal delay={0.15}>
        <p className="mt-6 max-w-2xl text-lg leading-relaxed text-white/70">
          Bring your brand to life with professional printing and creative services from MJG. Whether you
          need a small run of marketing materials, a large commercial print project, or help creating the
          artwork itself, our team can help manage the process from concept through production.
        </p>
        <p className="mt-4 max-w-2xl text-base leading-relaxed text-white/60">
          With digital printing, offset printing, professional finishing, and in-house graphic design
          services, MJG gives businesses one resource for producing polished, professional materials that
          support their brand.
        </p>
      </Reveal>
      <Reveal delay={0.3} className="mt-9 flex flex-col items-start gap-4 sm:flex-row sm:items-center">
        <Link
          to="/contact"
          data-testid="printing-hero-quote"
          className="group inline-flex items-center gap-2 bg-brand-red px-7 py-4 text-sm font-bold text-white transition-colors hover:bg-brand-redhover"
        >
          Request a Printing Quote <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </Link>
        <a
          href={COMPANY.phoneHref}
          data-testid="printing-hero-talk"
          className="inline-flex items-center gap-2 border border-white/25 px-7 py-4 text-sm font-bold text-white transition-colors hover:bg-white/5"
        >
          <Phone className="h-4 w-4" /> Talk to Our Team
        </a>
      </Reveal>
    </div>
  </section>
);

const ServiceAreas = () => (
  <section className="bg-white py-20 sm:py-28" data-testid="printing-service-areas">
    <div className="mx-auto max-w-7xl px-5">
      <SectionHeader
        eyebrow="Full-Service Printing"
        title="Printing Solutions for Your Brand"
        body="From everyday business materials to marketing campaigns, events, sales presentations, and promotional initiatives, MJG provides professional printing solutions tailored to the requirements of each project. Our team can help determine the appropriate production method, materials, quantities, and finishing options based on your objectives, timeline, and budget."
      />
      <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {SERVICE_AREAS.map((s, i) => (
          <Reveal key={s.t} delay={i * 0.06}>
            <div
              className="group flex h-full flex-col overflow-hidden rounded-sm border border-brand-line bg-brand-warm transition-all duration-300 hover:-translate-y-1.5 hover:border-brand-red hover:bg-white hover:shadow-xl"
              data-testid={`printing-area-${i}`}
            >
              <div className="overflow-hidden">
                <img src={s.img} alt={s.alt} className="aspect-[3/2] w-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
              </div>
              <div className="flex flex-1 flex-col p-6">
                <s.icon className="h-6 w-6 text-brand-red" aria-hidden="true" />
                <h3 className="mt-4 text-lg font-bold text-brand-black">{s.t}</h3>
                <p className="mt-2 text-sm leading-relaxed text-brand-gray">{s.d}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Digital = () => (
  <section className="bg-brand-warm py-20 sm:py-28" data-testid="printing-digital">
    <div className="mx-auto max-w-7xl px-5">
      <div className="grid items-center gap-12 lg:grid-cols-12">
        <div className="lg:col-span-6">
          <Eyebrow>Digital Printing</Eyebrow>
          <h2 className="mt-4 text-2xl font-extrabold tracking-tight text-brand-black sm:text-3xl lg:text-4xl">
            Fast. Flexible. Professional.
          </h2>
          <p className="mt-4 text-base leading-relaxed text-brand-gray">
            MJG's digital printing services provide professional full-color output with the flexibility
            required for today's marketing and business needs. Digital printing is particularly well suited
            for shorter production runs, fast turnaround requirements, multiple versions, and projects that
            may need frequent updates.
          </p>
          <h3 className="mt-8 text-sm font-bold uppercase tracking-wide text-brand-charcoal">Digital Printing Applications</h3>
          <div className="mt-4 flex flex-wrap gap-2">
            {DIGITAL_APPS.map((a) => <Chip key={a}>{a}</Chip>)}
          </div>
        </div>
        <Reveal delay={0.15} className="lg:col-span-6">
          <img
            src={IMAGES.printDigital}
            alt="Modern production digital press printing colorful commercial materials"
            className="aspect-[4/3] w-full rounded-sm object-cover"
            loading="lazy"
          />
        </Reveal>
      </div>
    </div>
  </section>
);

const Offset = () => (
  <section className="relative overflow-hidden bg-brand-black py-20 sm:py-28" data-testid="printing-offset">
    <div className="absolute inset-0 grid-overlay opacity-20" aria-hidden="true" />
    <div className="relative mx-auto max-w-7xl px-5">
      <div className="grid items-center gap-12 lg:grid-cols-12">
        <Reveal className="order-2 lg:order-1 lg:col-span-6">
          <img
            src={IMAGES.printOffset}
            alt="Commercial offset printing press in a professional print production facility"
            className="aspect-[4/3] w-full rounded-sm object-cover"
            loading="lazy"
          />
        </Reveal>
        <div className="order-1 lg:order-2 lg:col-span-6">
          <Eyebrow>Offset Printing</Eyebrow>
          <h2 className="mt-4 text-2xl font-extrabold tracking-tight text-white sm:text-3xl lg:text-4xl">
            Built for quality, consistency, and volume.
          </h2>
          <p className="mt-4 text-base leading-relaxed text-white/70">
            For larger-volume projects, offset printing provides excellent print quality, consistency, and
            cost efficiency. MJG can help clients determine whether offset production is appropriate based
            on quantity, paper selection, color requirements, finishing requirements, schedule, and overall
            project budget.
          </p>
          <h3 className="mt-8 text-sm font-bold uppercase tracking-wide text-white/50">Common Applications</h3>
          <ul className="mt-4 grid gap-x-6 gap-y-2.5 sm:grid-cols-2">
            {OFFSET_APPS.map((a) => (
              <li key={a} className="flex items-start gap-2.5 text-sm text-white/80">
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-brand-red" aria-hidden="true" /> {a}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  </section>
);

const Finishing = () => (
  <section className="bg-white py-20 sm:py-28" data-testid="printing-finishing">
    <div className="mx-auto max-w-7xl px-5">
      <div className="grid items-center gap-12 lg:grid-cols-12">
        <div className="lg:col-span-6">
          <Eyebrow>Professional Finishing</Eyebrow>
          <h2 className="mt-4 text-2xl font-extrabold tracking-tight text-brand-black sm:text-3xl lg:text-4xl">
            The Finishing Touch
          </h2>
          <p className="mt-4 text-base leading-relaxed text-brand-gray">
            Printing is only the beginning. The right finishing process can dramatically change the
            appearance, functionality, and presentation of a printed piece. Our team can coordinate
            finishing options based on the requirements of your project.
          </p>
          <h3 className="mt-8 text-sm font-bold uppercase tracking-wide text-brand-charcoal">
            Available finishing options may include
          </h3>
          <div className="mt-4 flex flex-wrap gap-2">
            {FINISHING_OPTIONS.map((o) => <Chip key={o}>{o}</Chip>)}
          </div>
        </div>
        <Reveal delay={0.15} className="lg:col-span-6">
          <img
            src={IMAGES.printFinishing}
            alt="Finished full-color printed pieces fanned out after professional finishing"
            className="aspect-[4/3] w-full rounded-sm object-cover"
            loading="lazy"
          />
        </Reveal>
      </div>
    </div>
  </section>
);

const Design = () => (
  <section className="bg-brand-warm py-20 sm:py-28" data-testid="printing-design">
    <div className="mx-auto max-w-7xl px-5">
      <div className="grid items-center gap-12 lg:grid-cols-12">
        <Reveal className="order-2 lg:order-1 lg:col-span-6">
          <img
            src={IMAGES.printDesign}
            alt="Color swatch books used by a graphic designer preparing print production artwork"
            className="aspect-[4/3] w-full rounded-sm object-cover"
            loading="lazy"
          />
        </Reveal>
        <div className="order-1 lg:order-2 lg:col-span-6">
          <Eyebrow>In-House Graphic Design</Eyebrow>
          <h2 className="mt-4 text-2xl font-extrabold tracking-tight text-brand-black sm:text-3xl lg:text-4xl">
            Need help turning an idea into something ready to print?
          </h2>
          <p className="mt-4 text-base leading-relaxed text-brand-gray">
            MJG's in-house graphic design services can help clients develop professional materials that look
            great and reproduce correctly in print. Customers can provide finished artwork or work with the
            MJG creative team to develop or modify a project.
          </p>
          <h3 className="mt-8 text-sm font-bold uppercase tracking-wide text-brand-charcoal">Graphic Design Services</h3>
          <div className="mt-4 flex flex-wrap gap-2">
            {DESIGN_SERVICES.map((s) => <Chip key={s}>{s}</Chip>)}
          </div>
        </div>
      </div>
    </div>
  </section>
);

const WhatWePrint = () => (
  <section className="bg-white py-20 sm:py-28" data-testid="printing-what-we-print">
    <div className="mx-auto max-w-7xl px-5">
      <SectionHeader eyebrow="Capabilities" title="What Can We Print for You?" />
      <div className="mt-12 grid gap-px overflow-hidden border border-brand-line bg-brand-line sm:grid-cols-2 lg:grid-cols-4">
        {PRINT_CATEGORIES.map((c, i) => (
          <Reveal key={c.t} delay={i * 0.06}>
            <div className="group h-full bg-brand-warm p-7 transition-colors hover:bg-white">
              <span className="font-mono text-sm font-semibold text-brand-red">0{i + 1}</span>
              <h3 className="mt-4 text-lg font-bold text-brand-black">{c.t}</h3>
              <ul className="mt-4 space-y-2">
                {c.items.map((it) => (
                  <li key={it} className="flex items-start gap-2 text-sm text-brand-gray">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-brand-red" aria-hidden="true" /> {it}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        ))}
      </div>
      <div className="mt-10 flex flex-col items-start gap-5 border border-brand-line bg-brand-warm p-7 sm:flex-row sm:items-center sm:justify-between">
        <p className="max-w-xl text-base font-semibold text-brand-black">
          Have something different in mind? Tell us about your project and we'll help determine the best way
          to produce it.
        </p>
        <CTAButton to="/contact" testId="printing-grid-cta" variant="primary">Discuss Your Project</CTAButton>
      </div>
    </div>
  </section>
);

const OneBrand = () => (
  <section className="relative overflow-hidden bg-brand-black py-20 sm:py-28" data-testid="printing-one-brand">
    <div className="absolute inset-0 grid-overlay opacity-20" aria-hidden="true" />
    <div className="relative mx-auto max-w-7xl px-5">
      <div className="grid gap-12 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <SectionHeader
            dark
            eyebrow="Beyond the Page"
            title="One Brand. One Partner."
            body="A successful brand extends across more than one product. MJG can help businesses maintain a consistent appearance across printed marketing materials, promotional products, branded merchandise, event materials, and other customer-facing materials."
          />
          <p className="mt-4 max-w-xl text-base leading-relaxed text-white/70">
            By combining printing, promotional products, and creative support through one partner, businesses
            can simplify purchasing while maintaining better consistency across their brand.
          </p>
          <div className="mt-6 flex flex-wrap gap-2">
            {["Printed Brochures", "Business Cards", "Branded Merchandise", "Promotional Items", "Event Collateral", "Branded Packaging"].map((x) => (
              <ChipDark key={x}>{x}</ChipDark>
            ))}
          </div>
          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <CTAButton to="/promotional-products-apparel" testId="printing-to-promo" variant="primary">
              Promotional Products
            </CTAButton>
            <CTAButton to="/marketing-collateral" testId="printing-to-collateral" variant="outlineLight">
              Marketing Collateral
            </CTAButton>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4 lg:col-span-7">
          <Reveal className="col-span-1">
            <img src={IMAGES.merchFlatlay} alt="Branded merchandise flatlay pairing printed materials with promotional products" className="aspect-[3/4] w-full rounded-sm object-cover" loading="lazy" />
          </Reveal>
          <div className="col-span-1 flex flex-col gap-4">
            <Reveal delay={0.1}>
              <img src={IMAGES.brochures} alt="Professionally printed brochures and marketing collateral" className="aspect-square w-full rounded-sm object-cover" loading="lazy" />
            </Reveal>
            <Reveal delay={0.2}>
              <img src={IMAGES.caps} alt="Branded embroidered caps matching printed brand materials" className="aspect-square w-full rounded-sm object-cover" loading="lazy" />
            </Reveal>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const HowItWorks = () => (
  <section className="bg-brand-warm py-20 sm:py-28" data-testid="printing-process">
    <div className="mx-auto max-w-7xl px-5">
      <SectionHeader eyebrow="How It Works" title="From Idea to Finished Product" />
      <ol className="mt-12 grid gap-px overflow-hidden rounded-sm border border-brand-line bg-brand-line sm:grid-cols-2 lg:grid-cols-5">
        {PROCESS_STEPS.map((step, i) => (
          <Reveal key={step.num} delay={i * 0.06}>
            <li className="group h-full bg-white p-7 transition-colors hover:bg-brand-black">
              <div className="flex items-center justify-between">
                <span className="font-mono text-sm font-semibold text-brand-red">{step.num}</span>
                <step.icon className="h-6 w-6 text-brand-charcoal transition-colors group-hover:text-white" aria-hidden="true" />
              </div>
              <h3 className="mt-8 text-lg font-bold text-brand-black transition-colors group-hover:text-white">{step.t}</h3>
              <p className="mt-2 text-sm leading-relaxed text-brand-gray transition-colors group-hover:text-white/70">{step.d}</p>
            </li>
          </Reveal>
        ))}
      </ol>
    </div>
  </section>
);

const WhyMJG = () => (
  <section className="bg-white py-20 sm:py-28" data-testid="printing-why-mjg">
    <div className="mx-auto max-w-7xl px-5">
      <SectionHeader eyebrow="Why MJG" title="More Than a Printer" />
      <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {WHY_BLOCKS.map((b, i) => (
          <Reveal key={b.t} delay={i * 0.06}>
            <div className="group h-full border border-brand-line bg-brand-warm p-7 transition-colors hover:border-brand-red hover:bg-white">
              <b.icon className="h-7 w-7 text-brand-red" aria-hidden="true" />
              <h3 className="mt-5 text-lg font-bold text-brand-black">{b.t}</h3>
              <p className="mt-2 text-sm leading-relaxed text-brand-gray">{b.d}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const ClosingCTA = () => (
  <section className="bg-brand-red" data-testid="printing-final-cta">
    <div className="mx-auto max-w-7xl px-5 py-20 sm:py-28">
      <div className="grid items-center gap-10 lg:grid-cols-2">
        <div>
          <Eyebrow className="text-white">Start Your Project</Eyebrow>
          <h2 className="mt-4 text-3xl font-extrabold leading-tight tracking-tight text-white sm:text-4xl lg:text-5xl">
            Let's Bring Your Next Project to Life.
          </h2>
          <p className="mt-5 max-w-lg text-lg leading-relaxed text-white/90">
            Whether you already have print-ready artwork or are starting with an idea, MJG can help you
            determine the right approach for your project. Talk with our team about your printing, design,
            finishing, and promotional requirements.
          </p>
        </div>
        <div className="flex flex-col gap-4 sm:flex-row lg:justify-end">
          <Link
            to="/contact"
            data-testid="printing-final-quote"
            className="group inline-flex items-center justify-center gap-2 bg-white px-7 py-4 text-sm font-bold text-brand-black transition-colors hover:bg-brand-black hover:text-white"
          >
            Request a Printing Quote <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
          <Link
            to="/contact"
            data-testid="printing-final-contact"
            className="inline-flex items-center justify-center gap-2 border border-white/40 px-7 py-4 text-sm font-bold text-white transition-colors hover:bg-white/10"
          >
            Contact MJG
          </Link>
        </div>
      </div>
    </div>
  </section>
);

export default function PrintingServices() {
  useSeo(
    "Printing Services | Digital, Offset, Finishing & Design | MJG Promotions",
    "MJG Promotions provides professional digital and offset printing, finishing, and in-house graphic design for business, marketing, promotional, and event materials."
  );
  return (
    <div data-testid="printing-page">
      <Hero />
      <ServiceAreas />
      <Digital />
      <Offset />
      <Finishing />
      <Design />
      <WhatWePrint />
      <OneBrand />
      <HowItWorks />
      <WhyMJG />
      <ClosingCTA />
    </div>
  );
}
